import React, { useState, useMemo } from 'react';
import { Box, Button, CircularProgress, Alert, Card, CardContent, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useMutation } from '@apollo/client';
import { PlanStep, Participant, PlanRole, IntakeStatus, StepKind } from '../../../../types/generated/graphql';
import { colors } from '../../../../theme/colors';
import { FeedItem } from '../StepInteractionFeed';
import DelegateTaskControl from '../../../shared/DelegateTaskControl';
import CollapsibleStepInfo from '../CollapsibleStepInfo';
import StepIntakePanel from '../../../StepIntakePanel';
import DynamicRequestForm from '../../../request-thread/DynamicRequestForm';
import { generateFieldsFromStep } from '../../../../utils/step-field-generator';
import { SUBMIT_STEP_INTAKE_INLINE, GET_PLAN } from '../../../../graphql/queries';
import { ActionPrompt, shouldShowActionPrompt } from './ActionPrompt';
import { StepChat } from './StepChat';
import ExpandStepButton from '../ExpandStepButton';
import DoThisForMeButton from '../../../shared/DoThisForMeButton';
import StepApprovalPrompt from '../StepApprovalPrompt';

interface ActiveWorkContentProps {
  planId: string;
  step: PlanStep;
  participants?: Participant[];
  roles?: PlanRole[];
  feedItems: FeedItem[];
  onThreadCreated?: () => void;
  /** Handler for marking step complete */
  onMarkComplete?: () => void;
  /** Handler for sending a chat message */
  onSendMessage?: (message: string) => void;
  /** Whether chat is currently loading */
  isChatLoading?: boolean;
}

/**
 * ACTIVE_WORK mode content.
 * User is actively working on the step.
 *
 * Renders:
 * - Step instructions/description (in feed)
 * - Inline form for structured inputs (decisions, confirmations, etc.)
 * - Delegation entry point
 * - Compact progress summary
 *
 * Does NOT render:
 * - Completion CTA
 * - Delegation status blocks
 */
export const ActiveWorkContent: React.FC<ActiveWorkContentProps> = ({
  planId,
  step,
  participants = [],
  roles = [],
  feedItems,
  onThreadCreated,
  onMarkComplete,
  onSendMessage,
  isChatLoading = false,
}) => {
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});

  // Check if this is a simple action step that should show the action prompt
  const showActionPrompt = useMemo(() => shouldShowActionPrompt(step), [step]);

  // Prefer step.allowedFields when present (resolved at plan creation); else generate from title/description
  const generatedFields = useMemo(() => {
    const allowed = step.allowedFields;
    if (allowed && allowed.length > 0) {
      const fields = allowed.map((f) => ({
        key: f.key,
        label: f.label,
        type: f.type,
        required: f.required,
        description: f.description ?? undefined,
        enumValues: f.enumValues ?? undefined,
      }));
      return { fields, uiMode: 'form' as const };
    }
    const result = generateFieldsFromStep(step.title, step.description);
    return { fields: result.fields, uiMode: result.uiMode };
  }, [step.title, step.description, step.allowedFields]);

  // Check if we should show an inline form (form-first UI mode)
  const showInlineForm = generatedFields.uiMode === 'form' && generatedFields.fields.length > 0;

  // Check if step has existing intake (already submitted or pending)
  const hasIntake = step.intake != null;
  const shouldShowDelegation = !hasIntake || step.intake?.status === IntakeStatus.Pending;

  // Submit inline form data
  const [submitInline, { loading: submitting, error: submitError }] = useMutation(
    SUBMIT_STEP_INTAKE_INLINE,
    {
      refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
      onCompleted: () => {
        setFormValues({});
      },
    }
  );

  const handleFieldChange = (key: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (Object.keys(formValues).length === 0) return;

    try {
      await submitInline({
        variables: {
          planId,
          stepId: step.id,
          input: {
            structuredData: formValues,
            rawText: null,
            fileIds: [],
          },
        },
      });
    } catch (err) {
      console.error('Failed to submit:', err);
    }
  };

  const hasFormData = Object.keys(formValues).some((key) => {
    const value = formValues[key];
    return value !== undefined && value !== null && value !== '';
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Collapsible Step Info - auto-expands first time */}
      {step.description && (
        <CollapsibleStepInfo stepId={step.id} description={step.description} />
      )}

      {/* Unified Step Chat - shows conversation history + input in one component */}
      {onSendMessage && (
        <StepChat
          step={step}
          feedItems={feedItems}
          onSendMessage={onSendMessage}
          isLoading={isChatLoading}
        />
      )}

      {/* Inline Form - structured inputs in a step tile (e.g. Contact information, Select agency) */}
      {showInlineForm && !hasIntake && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Card variant="outlined" sx={{ borderLeft: `4px solid ${colors.blue[400]}` }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {step.title}
              </Typography>
              {step.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {step.description}
                </Typography>
              )}
              <DynamicRequestForm
                allowedFields={generatedFields.fields}
                values={formValues}
                onChange={handleFieldChange}
                disabled={submitting}
              />

              {submitError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {submitError.message}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                  onClick={handleSubmit}
                  disabled={submitting || !hasFormData}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Show StepIntakePanel if step has intake data */}
      {hasIntake && (
        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.border}` }}>
          <StepIntakePanel planId={planId} step={step} />
        </Box>
      )}

      {/* Delegation - always available, and coexists only with pending intake */}
      {shouldShowDelegation && (
        <DelegateTaskControl
          planId={planId}
          step={step}
          participants={participants}
          showStatusOnly={false}
          onDelegationCreated={onThreadCreated ? () => onThreadCreated() : undefined}
        />
      )}

      {/* Approval Prompt - for steps requiring approval before execution */}
      <StepApprovalPrompt planId={planId} step={step} />

      {/* Automation Execution Controls - primary CTA for AUTOMATION/MIXED steps */}
      {(step.kind === StepKind.Automation || step.kind === StepKind.Mixed) && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <DoThisForMeButton
            planId={planId}
            step={step}
            variant="primary"
            size="small"
          />
          {onMarkComplete && (
            <Button
              variant="text"
              size="small"
              onClick={onMarkComplete}
              sx={{
                color: colors.gray[500],
                textTransform: 'none',
                fontSize: '0.75rem',
                '&:hover': { color: colors.gray[700] },
              }}
            >
              I did this manually
            </Button>
          )}
        </Box>
      )}

      {/* Action Prompt - for HUMAN steps (manual completion as primary CTA) */}
      {showActionPrompt && onMarkComplete && onSendMessage && (
        <ActionPrompt
          step={step}
          onMarkComplete={onMarkComplete}
          onSendMessage={onSendMessage}
        />
      )}

      {/* Expand Step into Subplan - always available to break down a step into a detailed plan */}
      <ExpandStepButton
        planId={planId}
        stepId={step.id}
        stepTitle={step.title}
        expansionPlanId={step.expansionPlanId}
      />
    </Box>
  );
};

export default ActiveWorkContent;
